import * as React from "react";

import {GqlRouter as WBDataGqlRouter} from "@khanacademy/wonder-blocks-data";
import type {GAEModule} from "@khan/data-gql-core";

import {GqlRouterContext} from "./gql-router-context.ts";
import {useGqlContext} from "./use-gql-context.ts";
import {wbdGqlFetch} from "./wbd-gql-fetch.ts";

interface Props {
    /**
     * The children to render within this component.
     */
    children: React.ReactNode;
    /**
     * The GAE module to use for GraphQL requests by children of this
     * component (until another GqlRouter overrides it).
     * Defaults to `"default"`.
     */
    module?: GAEModule;
}

/**
 * Configure GraphQL routing for Wonder Blocks Data-based GraphQL operations.
 *
 * This sets up the fetch for routing GraphQL mutations and queries to the
 * appropriate module. The given routing is used until a nested `GqlRouter`
 * changes that.
 */
export const GqlRouter = ({children, module}: Props): React.ReactElement => {
    /**
     * Here we get the current context so that we can re-use the fetch
     * assigned in any parent context. This allows mocks to propagate from
     * a test harness all the way down, even if a new router is used to change
     * the module.
     *
     * However, we always reset the `module` since that feels more expected for
     * devs that if they declare a new router, it's defaults are deterministic.
     * And there is little point in inheriting the `module` since that's the
     * only reason for specifying a `GqlRouter` anyway.
     */
    const {fetch: currentFetch} = React.useContext(GqlRouterContext);

    const thisModule = module || "default";

    const combineContext = useGqlContext();
    const thisDefaultContext = React.useMemo(
        () =>
            combineContext({
                module: thisModule,
            }),
        [combineContext, thisModule],
    );

    // We want to always use the same objects if things haven't changed to avoid
    // over-rendering consumers of our context, let's memoize the configuration.
    // By doing this, if a component under children that uses this context
    // uses React.memo, we won't force it to re-render every time we render
    // because we'll only change the context values if something has actually
    // changed.
    const thisFetch = React.useMemo(
        () => currentFetch ?? wbdGqlFetch,
        [currentFetch],
    );

    const thisGqlContext = React.useMemo(
        () => ({
            module: thisModule,
            fetch: currentFetch ?? wbdGqlFetch,
        }),
        [thisModule, currentFetch],
    );

    return (
        <WBDataGqlRouter fetch={thisFetch} defaultContext={thisDefaultContext}>
            <GqlRouterContext.Provider value={thisGqlContext}>
                {children}
            </GqlRouterContext.Provider>
        </WBDataGqlRouter>
    );
};
